/**
 * Google Business → Overview.
 *
 * How the listing is performing, and how the reviews stand.
 *
 * TWO HONESTY CONSTRAINTS, both Google's doing rather than ours:
 *
 *  - The figures run 2-3 DAYS BEHIND. The most recent days genuinely report
 *    zero, so the lag is stated on the page. Without that, a flat tail reads as
 *    a broken integration rather than data that has not arrived.
 *  - "Impressions" is not "profile views". It counts the listing appearing in
 *    results, which is much larger than the number of people who looked at it.
 *    The mockup called this profile views; that word is not used here, because
 *    it would overstate the number every time it was read.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  ArrowRight, CalendarCheck, Eye, MessageSquare, MousePointerClick, Navigation, Phone, Star,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { getErrorMessage } from '@/services/apiClient';
import {
  getGooglePerformance,
  listGoogleReviews,
  totalImpressions,
  type GooglePerformance,
} from '@/services/googleBusiness.service';

/**
 * Tile colours, written out in full rather than composed.
 *
 * Tailwind scans source text for complete class names, so a template string
 * like `bg-${tone}-50` produces no CSS at all. These have to be literal.
 *
 * The colours are an aid to scanning, not meaning: nothing here is good or bad,
 * so none of them is red or green in the "something is wrong" sense. Amber is
 * reserved for the rating, where a star is genuinely gold.
 */
const TONES = {
  blue: 'bg-blue-50 text-blue-600',
  violet: 'bg-violet-50 text-violet-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  sky: 'bg-sky-50 text-sky-600',
  amber: 'bg-amber-50 text-amber-600',
  indigo: 'bg-indigo-50 text-indigo-600',
  teal: 'bg-teal-50 text-teal-600',
} as const;

function Tile({
  icon,
  tone,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  tone: keyof typeof TONES;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${TONES[tone]}`}>
        {icon}
      </div>
      <p className="mt-3 text-2xl font-semibold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
      {hint && <p className="mt-0.5 text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}

const fmt = (n: number) => n.toLocaleString();

export function GoogleOverviewTab({ locationId }: { locationId: string }) {
  const [perf, setPerf] = useState<GooglePerformance | null>(null);
  const [rating, setRating] = useState<{ average: number; total: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The rating comes free with the reviews call we already make, rather
      // than a second analytics request for a number we already have.
      const [p, reviews] = await Promise.all([
        getGooglePerformance(locationId, 30),
        listGoogleReviews(locationId),
      ]);
      setPerf(p);
      setRating({ average: reviews.averageRating, total: reviews.totalReviewCount });
    } catch (e) {
      setError(getErrorMessage(e, 'Performance figures could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <SpinnerOverlay label="Loading performance…" />;

  if (error || !perf) {
    return (
      <EmptyState
        icon={<Eye className="h-10 w-10 text-slate-300" />}
        title="Performance could not be loaded"
        description={error ?? 'Something went wrong.'}
        action={{ label: 'Try again', variant: 'secondary', onClick: () => void load() }}
      />
    );
  }

  const m = perf.metrics;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          icon={<Eye className="h-4.5 w-4.5" />}
          tone="blue"
          label="Impressions"
          value={fmt(totalImpressions(perf))}
          hint="Times the listing appeared in Search or Maps"
        />
        <Tile
          icon={<MousePointerClick className="h-4.5 w-4.5" />}
          tone="violet"
          label="Website clicks"
          value={fmt(m.WEBSITE_CLICKS?.total ?? 0)}
        />
        <Tile
          icon={<Phone className="h-4.5 w-4.5" />}
          tone="emerald"
          label="Calls"
          value={fmt(m.CALL_CLICKS?.total ?? 0)}
        />
        <Tile
          icon={<Navigation className="h-4.5 w-4.5" />}
          tone="sky"
          label="Direction requests"
          value={fmt(m.BUSINESS_DIRECTION_REQUESTS?.total ?? 0)}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Tile
          icon={<Star className="h-4.5 w-4.5 fill-current" />}
          tone="amber"
          label="Average rating"
          value={rating ? rating.average.toFixed(1) : '—'}
        />
        <Tile
          icon={<MessageSquare className="h-4.5 w-4.5" />}
          tone="indigo"
          label="Total reviews"
          value={rating ? fmt(rating.total) : '—'}
        />
        <Tile
          icon={<CalendarCheck className="h-4.5 w-4.5" />}
          tone="teal"
          label="Bookings from the listing"
          value={fmt(m.BUSINESS_BOOKINGS?.total ?? 0)}
        />
      </div>

      {/* Stated, not buried: the last few days really are zero. */}
      <p className="text-xs text-slate-500">
        {perf.dateRange.startDate} to {perf.dateRange.endDate} · {perf.dataDelay}, so the most
        recent days may still show as zero.
      </p>

      {/*
        Posting lives in Social Posts, not here.

        Google Business is already a publishing target there, with the media
        rules, scheduling and per-network validation the composer has. A second
        composer would be a second thing to keep correct for no gain — and a
        Content tab could not have shown how a post performed anyway, since
        Google deprecated per-post analytics with no replacement.
      */}
      <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-4">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900">Posting to Google Business</h3>
          <p className="mt-0.5 text-sm text-slate-600">
            Google posts are written in Social Posts, alongside your other networks. Google no
            longer reports how individual posts performed, so only the listing figures above are
            available.
          </p>
        </div>
        <Link to="/marketing/social" className="shrink-0">
          <Button variant="secondary" size="sm" rightIcon={<ArrowRight className="h-4 w-4" />}>
            Go to Social Posts
          </Button>
        </Link>
      </div>
    </div>
  );
}
